/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentChildren, ComponentProps } from 'preact';

import { List } from '#PRIVATE_List';
import type { DataState, ListItemRendererContext, Metadata } from '#UNSAFE_Collection';
import { Flex } from '#UNSAFE_Flex';
import { HighlightText } from '#UNSAFE_HighlightText';
import { ListItemText } from '#UNSAFE_ListItemLayout';
import type { Item } from '#utils/UNSAFE_dataProvider';
import type { Keys } from '#utils/UNSAFE_keys';
import type { ItemTextType } from '#utils/UNSAFE_selectUtils';
import { useCallback, useMemo } from 'preact/hooks';
import { type ComponentName, renderItemText } from './itemTextUtils';
import { dropdownStyles } from './themes/dropdownStyles.css';

type ListProps<K extends string | number, D> = ComponentProps<typeof List<K, D>>;
type PickedPropsFromList = Pick<
  ComponentProps<typeof List>,
  'aria-label' | 'currentItemVariant' | 'selectionMode'
>;

// pick the props from the ListItemContext instead of extending it directly so that we
// don't automatically expose any new props that may be added to it
type PickedPropsFromListItemContext<K, D> = Pick<
  ListItemRendererContext<K, D>,
  'data' | 'index' | 'selector'
>;
type ItemRendererSelectionDetail<K> = {
  value: Set<K>;
  target: EventTarget | null;
};
type ItemRendererMetadata<K> = Pick<Metadata<K>, 'key' | 'suggestion'>;
type ItemRendererProps<K, D> = PickedPropsFromListItemContext<K, D> & {
  metadata: ItemRendererMetadata<K>;
  searchText?: string;
  selectedKeys?: Set<K>;
  onSelectionChange: (detail: ItemRendererSelectionDetail<K>) => void;
};

type DefaultListCurrentKeyDetail<K> = { value: K };
type DefaultListItemActionDetail<K, D> = { context: Item<K, D> };
type DefaultListSelectionDetail<K> = { value: Set<K> };

type Props<K extends string | number, D extends Record<string, any>> = PickedPropsFromList & {
  itemRenderer?: (itemRendererProps: ItemRendererProps<K, D>) => ComponentChildren;
  itemText: ItemTextType<K, D>;
  // props from List that we constrain or unconstrain
  currentKey?: K;
  data?: DataState<K, D> | null;
  onCurrentKeyChange: (detail: DefaultListCurrentKeyDetail<K>) => void;
  onItemAction?: (detail: DefaultListItemActionDetail<K, D>) => void;
  onLoadRange?: ComponentProps<typeof List>['onLoadRange'];
  onSelectionChange?: (detail: DefaultListSelectionDetail<K>) => void;
  searchText?: string;
  selectedKeys?: Set<K>;
  // props for logging purposes
  componentName: ComponentName;
};

const noOp = () => {};

export function DefaultList<K extends string | number, D extends Record<string, any>>({
  'aria-label': ariaLabel,
  componentName,
  currentItemVariant,
  currentKey,
  data,
  itemRenderer,
  itemText,
  onCurrentKeyChange,
  onItemAction,
  onLoadRange = noOp,
  onSelectionChange,
  searchText,
  selectedKeys,
  selectionMode
}: Props<K, D>) {
  const listItemRenderer = useCallback(
    (listItemContext: ListItemRendererContext<K, D>) => {
      // specify the listItemContext props that we want to expose individually instead of
      // spreading the whole object so that we don't automatically include any new props that
      // may be added to it
      const itemRendererProps: ItemRendererProps<K, D> = {
        data: listItemContext.data,
        index: listItemContext.index,
        metadata: listItemContext.metadata,
        selector: listItemContext.selector,
        searchText,
        selectedKeys,
        onSelectionChange: ({ value }) => {
          onSelectionChange?.({ value });
        }
      };
      const itemContext = {
        data: listItemContext.data,
        key: listItemContext.metadata.key,
        metadata: listItemContext.metadata
      };
      return itemRenderer ? (
        itemRenderer(itemRendererProps)
      ) : (
        <Flex align="center">
          {listItemContext.selector && (
            <div class={dropdownStyles.selectorWrapper}>{listItemContext.selector()}</div>
          )}
          <div
            class={
              selectionMode === 'single'
                ? dropdownStyles.singleSelectItem
                : dropdownStyles.multiSelectItem
            }>
            <ListItemText variant="primary">
              <HighlightText matchText={searchText}>
                {renderItemText(itemContext, itemText, componentName) as string}
              </HighlightText>
            </ListItemText>
          </div>
        </Flex>
      );
    },
    [
      componentName,
      itemRenderer,
      itemText,
      onSelectionChange,
      searchText,
      selectedKeys,
      selectionMode
    ]
  );

  const keys = useMemo(() => {
    return { all: false, keys: selectedKeys ?? new Set<K>() };
  }, [selectedKeys]) as Keys<K>;

  const currentItemOverride = useMemo(
    () => (currentKey !== undefined ? { rowKey: currentKey } : undefined),
    [currentKey]
  );
  const handleItemAction = useCallback<NonNullable<ListProps<K, D>['onItemAction']>>(
    ({ context: { data, metadata } }) => {
      onItemAction?.({ context: { data, key: metadata.key, metadata } });
    },
    [onItemAction]
  );
  const handleSelectionChange = useCallback<NonNullable<ListProps<K, D>['onSelectionChange']>>(
    ({ value }) => {
      onSelectionChange?.({ value: new Set(value.keys?.values()) });
    },
    [onSelectionChange]
  );

  return (
    <List<K, D>
      aria-label={ariaLabel}
      allowTabbableMode={false}
      currentItemVariant={currentItemVariant}
      currentItemOverride={currentItemOverride}
      data={data ?? null}
      onPersistCurrentItem={onCurrentKeyChange}
      onItemAction={handleItemAction}
      onLoadRange={onLoadRange}
      onSelectionChange={handleSelectionChange}
      role="listbox"
      selectedKeys={keys}
      selectionMode={selectionMode}>
      {(context: ListItemRendererContext<K, D>) => listItemRenderer(context)}
    </List>
  );
}
